/**
 * The one way to read a REACT_APP_* value. Use this instead of runtimeEnv()
 * directly - reading runtimeEnv() alone is silently broken on phones.
 *
 * Why: runtimeEnv() comes from @mars/heroku-js-runtime-env, which in a
 * production build does not read process.env at all. It parses a placeholder
 * string that the Heroku node server rewrites in the JS as it serves it
 * (server.js). That works on web. Capacitor serves the built files straight
 * off the filesystem inside the .ipa, so nothing ever rewrites the
 * placeholder, JSON.parse throws, and runtimeEnv() returns {} on every native
 * launch.
 *
 * The cost of that was not theoretical: the faster-dashboard flag was read
 * this way, so it was false on every phone from the day it shipped. It was
 * switched on in production believing it was live, and it had never once run
 * on a device - while the per-category fan-out it was meant to replace kept
 * rate-limiting the home screen against itself (869efwdbk, 869eafq50).
 *
 * Order depends on the platform, and that distinction is the whole point:
 *
 *  - NATIVE reads process.env only. REACT_APP_* is inlined at BUILD time by
 *    react-scripts, so it survives into the .ipa. runtimeEnv() cannot work
 *    there at all (nothing rewrites the placeholder inside the bundle).
 *
 *  - WEB reads runtimeEnv() FIRST, falling back to the build-time value.
 *    This used to be the other way round, which quietly defeated the entire
 *    mechanism: react-scripts inlines a value whenever one is set at build
 *    time, so the build-time value was ALWAYS present and the Heroku config
 *    var could never override anything, despite that being the documented
 *    reason runtimeEnv is here.
 *
 *    That was not cosmetic. Production web is released by promoting the UAT
 *    slug, and the promoted slug carries UAT's build-time values with it. So
 *    app.doqit.io served a bundle hard-wired to uat-doqit-api.herokuapp.com
 *    and every sign-in got a 422, because people's accounts live in the
 *    production database, not UAT's. Production's own REACT_APP_API_URL was
 *    correct the entire time and was simply never consulted. Reading runtime
 *    first is what makes promoting a slug safe.
 *
 * Note the consequence for flags on native: a value baked in at build time
 * cannot be toggled remotely, so a native flag change needs a new build
 * either way. That is a property of the platform, not of this helper - but it
 * is the reason a flag is often the wrong tool for native and one code path
 * is the right one.
 */
import { Capacitor } from '@capacitor/core';
import runtimeEnv from '@mars/heroku-js-runtime-env';

type EnvKey = `REACT_APP_${string}`;

const readBuildTime = (key: EnvKey): string | undefined => {
  const value = (process.env as Record<string, string | undefined>)[key];
  return value === '' ? undefined : value;
};

const readRuntime = (key: EnvKey): string | undefined => {
  try {
    const value = (runtimeEnv() as Record<string, string | undefined>)[key];
    return value === '' ? undefined : value;
  } catch {
    // runtimeEnv() parses a placeholder the Heroku server rewrites as it
    // serves. Anywhere that has not happened it throws, and a missing config
    // value must never take the app down.
    return undefined;
  }
};

export const getEnv = (key: EnvKey): string | undefined => {
  if (Capacitor.isNativePlatform()) {
    return readBuildTime(key);
  }

  return readRuntime(key) ?? readBuildTime(key);
};

/** Truthiness helper for on/off flags, so call sites don't each invent one. */
export const getEnvFlag = (key: EnvKey): boolean => {
  const value = getEnv(key);
  if (value === undefined) return false;
  return value !== 'false' && value !== '0';
};
